import type { AppStatusEnum, AppTypeEnum } from "@verbleif/lib/src/composables/Api/Type/AppStore/app.enum";
import type { AppDeveloperProfileShareCapability } from "@verbleif/lib/src/composables/Api/Type/AppStore/app.interface";
import type { PermissionService } from "../Permissions/PermissionService";
import { PermissionAttributesEnum } from "@verbleif/lib/src/composables/Permissions/permissions.enum";
import { computed, watch } from "vue";
import { useAuthStore } from "../AuthStore/useAuthStore";
import { isCmsContext } from "../isCms";
import { useLocationStore } from "../LocationStore/useLocationStore";
import { appVoter, permissionService } from "../Permissions/SharedPermissions";
import { AppVoter } from "../Permissions/Voters/AppVoter";
import { isOutboundApp } from "./appDirection";
import { developerShare } from "./developerShareCapability";
import { readsInstallsOfClient } from "./readsInstallsOfClient";
import { developerCapabilityFor, hasPersonalInstalls, loadMyAppStoreAccess } from "./useMyAppStoreAccess";

/**
 * Mirrors the backend AppSubscription `_read_collection_via_client` gate (api repo
 * `AppSubscriptionVoter::canReadCollectionViaClient`, behind `GET /clients/{client}/app_subscriptions`):
 * a user may open the App Store / Integrations marketplace for a client when they hold the global
 * `app_subscription.read_any` permission OR a client-scoped `app_subscription.read` on that client.
 *
 * The gate moved off the removed `GET /clients/{client}/apps` collection onto the client-scoped installs
 * collection — the endpoint the grid now joins install state against — so access matches exactly what
 * the page can actually read. Behaviour is otherwise unchanged: a pure collection-access check for the
 * active organization (it must never grant access just because some public app happens to be published).
 */
export function canReadAppCollectionViaClient(
  clientIri: string | null | undefined,
  service: PermissionService = permissionService,
): boolean {
  return readsInstallsOfClient(clientIri, service);
}

/**
 * Whether a marketplace catalog row may be opened in the read-only AppDetail product view. The grid
 * already lists only apps the backend returned on `GET /api/apps` for a user who passed the collection
 * gate (`canReadAppCollectionViaClient`); do not layer a stricter per-app read voter on top of that —
 * especially the CMS-only global `app.read_any` override, which would leave cards visible but
 * unclickable outside the CMS shell.
 */
export function canViewListedApp(
  app: { "status"?: AppStatusEnum, "client"?: string | null, "@id"?: string, "location"?: string | null },
  clientIri: string | null | undefined,
  service: PermissionService = permissionService,
): boolean {
  const voter = new AppVoter(service);
  if (voter.canRead(app)) {
    return true;
  }

  return canReadAppCollectionViaClient(clientIri, service);
}

interface AppDeveloperScope {
  "@id"?: string
  "client"?: string | null
  "location"?: string | null
  "type"?: AppTypeEnum
}

/**
 * Outbound App-definition CRUD (status, category, connector documents, the full auth config, …) is owned
 * entirely by the CMS; the web App Store's editor (AppFields, opened via AppEdit or the inline Developer
 * tab) is deliberately incomplete for outbound apps. Every web entry point that opens or exposes that
 * editor — AppDetail's Edit button, the Manage tab's "Edit app", the marketplace card's ⋯ Edit, the
 * Developer tab's "My apps" edit, and the inline Developer tab itself — must hide for an outbound app
 * OUTSIDE the CMS shell, even for a holder of a global wildcard override. Inside the CMS
 * (GlobalAppsAdminOverview, or a client's embedded App Store tab in ClientEdit) the full editor stays
 * available exactly as before — this never restricts CMS.
 */
export function isOutboundAppEditRestrictedOnWeb(app: { type?: AppTypeEnum } | null | undefined): boolean {
  return !isCmsContext() && isOutboundApp(app);
}

/**
 * Developer access ("Ontwikkelaarstoegang"): the `website_developer` bit of the signed-in user's allowed apps,
 * which only platform staff hand out. The API reports it on `/users/me` as `developerAccess`.
 */
export function hasDeveloperAccess(): boolean {
  return useAuthStore().user.value?.developerAccess === true;
}

/** The global app overrides, which the API lets create apps and accept the agreement without developer access. */
function hasGlobalAppOverride(service: PermissionService): boolean {
  return !!service.getGlobalPermissionForUser({ attribute: PermissionAttributesEnum.GlobalOperationAppCreateany })
    || !!service.getGlobalPermissionForUser({ attribute: PermissionAttributesEnum.GlobalOperationAppUpdateany });
}

/**
 * Client-scoped developer surface for the web App Store (never CMS): create, update, inbound auth-method choice, or
 * config `data` write on the active organization, or a developer profile share of that organization (every
 * capability may open the Developer tab; what it may change there follows the capability), or a global app override.
 *
 * Developer access is not needed to open the tab: the API lets these grants read and edit the developer profile and
 * the organisation's apps without it. Only creating an app and accepting the Developer Agreement take developer
 * access ({@link canCreateDeveloperApp}, {@link canAcceptDeveloperAgreement}); without it the tab asks Verbleif for it
 * in place of "Nieuwe app". Without developer access the tab also needs the organisation's app read, which its
 * developer profile and agreement requests ask: a create grant alone would then only open a tab of 403s.
 */
export function canAccessDeveloperSurface(
  clientIri: string | null | undefined,
  service: PermissionService = permissionService,
  shareCapability: AppDeveloperProfileShareCapability | null = developerCapabilityFor(clientIri),
  developerAccess: boolean = hasDeveloperAccess(),
): boolean {
  if (!clientIri) {
    return false;
  }

  if (shareCapability !== null || hasGlobalAppOverride(service)) {
    return true;
  }

  const voter = new AppVoter(service);
  const mutatesApps = voter.canCreate({ client: clientIri }) || voter.canUpdate({ client: clientIri });

  if (mutatesApps && developerAccess) {
    return true;
  }

  return (mutatesApps || hasFieldWriteOnly(voter, clientIri)) && readsOrganisationApps(voter, clientIri);
}

/** The auth-method or config `data` field write on the organization's apps, without create or update. */
function hasFieldWriteOnly(voter: AppVoter, clientIri: string): boolean {
  return voter.canChooseAuthMethod(clientIri) || voter.canWriteData(clientIri);
}

/**
 * The app read of the organization (or the global read override). A field write alone does not let the Developer
 * tab load: its developer profile and developer agreement need this read, so without it the tab would only show
 * 403s.
 */
function readsOrganisationApps(voter: AppVoter, clientIri: string): boolean {
  return voter.canRead({ client: clientIri });
}

/**
 * Mirrors the API's `AppVoter` create rule: developer access plus app create on the organization, or the global
 * create override on its own. A developer profile share never creates apps.
 */
export function canCreateDeveloperApp(
  clientIri: string | null | undefined,
  service: PermissionService = permissionService,
  developerAccess: boolean = hasDeveloperAccess(),
): boolean {
  if (!clientIri) {
    return false;
  }

  if (service.getGlobalPermissionForUser({ attribute: PermissionAttributesEnum.GlobalOperationAppCreateany })) {
    return true;
  }

  return developerAccess && new AppVoter(service).canCreate({ client: clientIri });
}

/**
 * Whether the developer profile (company details) may be edited. Mirrors the API's `AppDeveloperProfileVoter`:
 * app create or update on the organization, or a share that edits company fields. Accepting the Developer
 * Agreement also takes developer access ({@link canAcceptDeveloperAgreement}). The auth-method or data-write
 * grants alone open the Developer tab but do not edit the profile.
 */
export function canEditDeveloperProfile(
  clientIri: string | null | undefined,
  service: PermissionService = permissionService,
  shareCapability: AppDeveloperProfileShareCapability | null = developerCapabilityFor(clientIri),
): boolean {
  if (!clientIri) {
    return false;
  }

  const voter = new AppVoter(service);

  return voter.canCreate({ client: clientIri })
    || voter.canUpdate({ client: clientIri })
    || developerShare.allowsCompanyFieldEdit(shareCapability);
}

/**
 * Mirrors the API's `DeveloperAgreementVoter` accept rule: developer access plus app create or update on the
 * organization, or a global app override on its own; never a share.
 */
export function canAcceptDeveloperAgreement(
  clientIri: string | null | undefined,
  service: PermissionService = permissionService,
  developerAccess: boolean = hasDeveloperAccess(),
): boolean {
  if (!clientIri) {
    return false;
  }

  if (hasGlobalAppOverride(service)) {
    return true;
  }

  if (!developerAccess) {
    return false;
  }

  const voter = new AppVoter(service);

  return voter.canCreate({ client: clientIri }) || voter.canUpdate({ client: clientIri });
}

/**
 * Whether the App Store opens at all for the organization: its marketplace (install read), the Developer tab
 * (developer grants or a developer profile share), or the installs the person owns or was given a share on.
 * Share-based reach needs {@link loadMyAppStoreAccess} to have run for the organization.
 */
export function canOpenAppStore(
  clientIri: string | null | undefined,
  service: PermissionService = permissionService,
): boolean {
  return canReadAppCollectionViaClient(clientIri, service)
    || canAccessDeveloperSurface(clientIri, service)
    || hasPersonalInstalls(clientIri);
}

/**
 * Per-app developer editing inside AppDetail / the inline Developer tab: update, inbound auth-method
 * choice, or config `data` write on the app's owning client. CMS-only field gates stay inside AppFields.
 */
export function canAccessAppDeveloperEditing(
  app: AppDeveloperScope | null | undefined,
  service: PermissionService = permissionService,
  shareCapability: AppDeveloperProfileShareCapability | null = developerCapabilityFor(app?.client),
): boolean {
  if (!app) {
    return false;
  }

  // CMS owns outbound App-definition CRUD; the web editor is deliberately incomplete for outbound apps.
  // Short-circuit before any grant check so not even a global wildcard override reopens it on the web.
  if (isOutboundAppEditRestrictedOnWeb(app)) {
    return false;
  }

  const voter = new AppVoter(service);

  // Client-independent grants first, so a platform admin reaches the Developer tab even on a
  // Verbleif-owned app that has no owning client (e.g. the built-in BookingExperts / Camping.care
  // integrations, whose `client` is null). `canUpdate` honours the backend `app.update_any` override
  // (client-agnostic by design), and the CMS-only advanced-field gates (status / type / category /
  // internal id / full auth config) are likewise global. Rate-limit rights alone do not open the editor:
  // the rate limit has its own endpoint and the editor only opens for users who may update the app.
  if (voter.canUpdate(app)
    || voter.canConfigureAuth()
    || voter.canWriteStatus()
    || voter.canWriteType()
    || voter.canWriteCategory()
    || voter.canWriteInternalIdentifier()) {
    return true;
  }

  // Client-scoped developer grants (web app store / app owner) require the app's owning client.
  if (!app.client) {
    return false;
  }

  // A developer profile share that may edit the listing opens the editor too; the editor itself limits a
  // marketing-only share to the listing fields.
  // A field write opens the editor only with the organization's app read, which its developer profile and
  // agreement requests need.
  return (hasFieldWriteOnly(voter, app.client) && readsOrganisationApps(voter, app.client))
    || developerShare.allowsMarketing(shareCapability);
}

/** The app-update check the editor gates use: the shared AppVoter in the app, a voter over a test service in specs. */
type AppUpdateCheck = Pick<AppVoter, "canUpdate">;

/**
 * The app editor's change rights for an app: `full` editing for update holders and shares that manage the app,
 * `marketing` for a marketing-only share (the listing fields), `none` otherwise.
 */
export function appEditScope(
  app: AppDeveloperScope | null | undefined,
  voter: AppUpdateCheck = appVoter,
  shareCapability: AppDeveloperProfileShareCapability | null = developerCapabilityFor(app?.client),
): "full" | "marketing" | "none" {
  if (!app) {
    return "none";
  }

  if (voter.canUpdate(app) || developerShare.allowsAppNonMarketingManage(shareCapability)) {
    return "full";
  }

  return developerShare.allowsMarketing(shareCapability) ? "marketing" : "none";
}

/** Who sees an app's installations and their traffic: update holders and shares that read logs (API `_VIEW_INSTALLATIONS`). */
export function canViewAppInstallations(
  app: AppDeveloperScope | null | undefined,
  voter: AppUpdateCheck = appVoter,
  shareCapability: AppDeveloperProfileShareCapability | null = developerCapabilityFor(app?.client),
): boolean {
  if (!app) {
    return false;
  }

  return voter.canUpdate(app) || developerShare.allowsLogs(shareCapability);
}

/**
 * Who sees an app's activity feed, as the API's Activity `_READ_COLLECTION_VIA_APP` decides it: the app read on the
 * app's organization (or the global read override) and shares that read logs. A marketing-only share does not.
 */
export function canViewAppActivity(
  app: AppDeveloperScope | null | undefined,
  voter: Pick<AppVoter, "canRead"> = appVoter,
  shareCapability: AppDeveloperProfileShareCapability | null = developerCapabilityFor(app?.client),
): boolean {
  if (!app) {
    return false;
  }

  // Without the status the read is the organization's app read, not the PUBLISHED fast path anyone has.
  return voter.canRead({ client: app.client ?? null }) || developerShare.allowsLogs(shareCapability);
}

/**
 * Reactive App Store / Integrations access for the currently-selected organization (the client behind
 * the selected location). Drives the Integrations sidebar entry's disabled "no access" state and the
 * route guard so a user who would be 403'd by the backend never opens the page. Besides the person's
 * permissions it counts what they reach through shares, loaded for the organization when it changes.
 */
export function useAppStoreAccess(service: PermissionService = permissionService) {
  const locationStore = useLocationStore();

  const clientIri = computed<string | null>(() => locationStore.selectedLocationObject.value?.client ?? null);

  watch(clientIri, (iri) => {
    void loadMyAppStoreAccess(iri);
  }, { immediate: true });

  const canAccessAppStore = computed<boolean>(() => canOpenAppStore(clientIri.value, service));

  return { clientIri, canAccessAppStore };
}
